import { buttonVariants } from "@loot-os/ui/components/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@loot-os/ui/components/card";
import { Input } from "@loot-os/ui/components/input";
import { Label } from "@loot-os/ui/components/label";
import { useQuery } from "@tanstack/react-query";
import { ExternalLinkIcon } from "lucide-react";
import { useState } from "react";

import { CopyButton } from "../components/DetailFields";
import { ErrorAlert } from "../components/ErrorAlert";
import { parseShopHandle, shopifyInstallUrl } from "../lib/shopifyStore";
import { useTRPC } from "../lib/trpc";

/**
 * The install link for this stage's Shopify app on one store, to send a merchant or open
 * yourself: how an unlisted app (dev, a developer's own, production before its listing) reaches
 * a store. Installing provisions the store's own tenant (docs/shopify.md). Platform admins only;
 * it reads the app's public client id and writes nothing.
 */
export function PlatformShopifyInstallPage() {
  const trpc = useTRPC();
  const app = useQuery(trpc.platform.shopify.app.queryOptions());
  const [storeInput, setStoreInput] = useState("");
  const handle = parseShopHandle(storeInput);
  const clientId = app.data?.clientId ?? null;
  const link = handle && clientId ? shopifyInstallUrl(handle, clientId) : null;

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Shopify install link</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          The link that installs this stage's Shopify app on one store. Installing sets the store up
          as a tenant of its own.
        </p>
      </div>
      {app.isLoading && <div className="text-sm text-muted-foreground">Loading…</div>}
      {app.error && <ErrorAlert message={app.error.message} />}
      {app.data && (
        <Card>
          <CardHeader>
            <CardTitle>Install link</CardTitle>
            <CardDescription>
              An app without a listing installs on development stores only.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-5">
            {!clientId ? (
              <p className="text-sm text-muted-foreground">
                The Shopify app is not configured on this stage.
              </p>
            ) : (
              <>
                <div className="flex flex-col gap-2">
                  <Label htmlFor="shopify-install-store">Store</Label>
                  <Input
                    id="shopify-install-store"
                    className="max-w-sm"
                    placeholder="your-store.myshopify.com"
                    value={storeInput}
                    aria-invalid={storeInput.trim() && !handle ? true : undefined}
                    onChange={(e) => setStoreInput(e.currentTarget.value)}
                  />
                  <p className="text-xs text-muted-foreground">
                    {storeInput.trim() && !handle
                      ? "That is not a store: paste its handle, myshopify domain or an admin URL."
                      : "The handle, the myshopify domain or any admin URL of the store."}
                  </p>
                </div>
                {link && (
                  <div className="flex flex-col gap-2">
                    <span className="text-sm font-medium">Link</span>
                    <div className="flex min-w-0 items-center gap-2">
                      <code className="min-w-0 flex-1 truncate rounded-md border bg-muted px-2.5 py-1.5 font-mono text-xs">
                        {link}
                      </code>
                      <CopyButton text={link} label="Copy link" />
                    </div>
                    <a
                      href={link}
                      target="_blank"
                      rel="noreferrer"
                      className={buttonVariants({ variant: "outline", className: "w-fit" })}
                    >
                      Open in Shopify
                      <ExternalLinkIcon data-icon="inline-end" />
                    </a>
                  </div>
                )}
                <p className="text-xs text-muted-foreground">
                  App client ID <code className="font-mono">{clientId}</code>
                </p>
              </>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
